import { useCallback, useRef, useState } from 'react';

import api, { FetchError, FetchRequest } from '../../common/api/api';
import { useApiGet } from '../../common/api/Api.hooks';
import { apiResolve } from '../../common/api/apiResolve';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { removeUndefinedProps } from '../../common/utils/object.utils';
import { VersionItem } from '../../common/version-item/Version.interface';
import {
  TestRailApiAddCaseData,
  TestRailApiProject,
  TestRailApiTestCase,
  TestRailCase
} from '../test-rail/TestRail.interface';
import { TaskV2LinkFileVersionsParams } from './TaskV2.interface';

export function useGetTaskFileVersionsV2(taskId: number) {
  const { data, isLoading, error, get } = useApiGet<VersionItem[]>(
    `tasks-v2/${taskId}/file-versions`
  );
  return { files: data, isLoading, error, findFiles: get };
}

export function useLinkTaskFileVersionsV2() {
  // preserve the request for aborting
  const requestRef = useRef<FetchRequest>();
  const [state, setState] = useState<{
    data?: { added: number; removed: number };
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const abortLinkTaskFileVersions = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = undefined;
  }, []);

  const linkTaskFileVersions = useCallback(
    async (taskId: number, params: TaskV2LinkFileVersionsParams) => {
      abortLinkTaskFileVersions();
      setState({ isLoading: false });

      const request = api.patch(`tasks-v2/${taskId}/file-versions`, params);
      requestRef.current = request;
      const result = await apiResolve<{ added: number; removed: number }>(
        request
      );
      const { data, error } = result;

      // remove request when done
      requestRef.current = undefined;
      setState({ data, isLoading: false, error });

      return result;
    },
    [abortLinkTaskFileVersions]
  );

  return {
    ...state,
    linkTaskFileVersions,
    abortLinkTaskFileVersions,
    setState
  };
}

// non-hook implementation
export function findTaskV2TestRailTestCases(
  taskId: number,
  params: {
    page?: number;
    limit?: number;
    order?: string;
    direction?: 'ASC' | 'DESC';
  }
) {
  const request = api.get(
    `tasks-v2/${taskId}/testrail/cases`,
    removeUndefinedProps(params)
  );
  return { request, result: apiResolve<PaginatedList<TestRailCase>>(request) };
}

export function useCreateTaskV2TestRailTestCase() {
  // preserve the request for aborting
  const requestRef = useRef<FetchRequest>();

  const [state, setState] = useState<{
    testCase?: TestRailCase;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const abortCreateTestCase = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = undefined;
  }, []);

  const createTestCase = useCallback(
    async (options: {
      taskId: number;
      project: TestRailApiProject;
      /** TestRail add test case data. */
      data: TestRailApiAddCaseData;
    }) => {
      abortCreateTestCase();
      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const request = api.post(`tasks-v2/${options.taskId}/testrail/cases`, {
        project: options.project,
        data: options.data
      });
      requestRef.current = request;
      const result = apiResolve<TestRailCase>(request);
      const { data, error } = await result;

      // remove request when done
      requestRef.current = undefined;
      setState({ testCase: data, isLoading: false, error });

      return result;
    },
    [abortCreateTestCase]
  );

  return { ...state, createTestCase, abortCreateTestCase, setState };
}

// non-hook implementation
export function linkTaskV2TestRailTestCases(
  taskId: number,
  params: {
    project: TestRailApiProject;
    testCases: TestRailApiTestCase[];
    /**
     * Determines if the TaskV2 TestRail test cases
     * should replaced with the provided test cases.
     */
    replace?: boolean;
  }
) {
  const request = api.patch(
    `tasks-v2/${taskId}/testrail/cases`,
    removeUndefinedProps(params)
  );
  return { request, result: apiResolve<{ affected: number }>(request) };
}

// non-hook implementation
export function unlinkTaskV2TestRailTestCases(
  taskId: number,
  params: { testCaseIds: number[] }
) {
  const request = api.delete(`tasks-v2/${taskId}/testrail/cases`, {
    ...removeUndefinedProps(params),
    // comma separated ids
    testCaseIds: params.testCaseIds.join(',')
  });
  return { request, result: apiResolve<{ affected: number }>(request) };
}
